import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ScanBarcode, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBarcodeScanner } from "@/hooks/useBarcodeScanner";
import { CameraScanButton } from "@/components/scan/CameraScanButton";
import { codeVariants } from "@/lib/cameraScan";
import { GrnTotals } from "@/components/inventory/GrnView";
import { formatMoney, toMinor } from "@/lib/money";

const n = (v) => (/^\d+$/.test(String(v).trim()) ? parseInt(v, 10) : 0);
const today = () => new Date().toISOString().slice(0, 10);
const blankLine = (p) => ({ product_id: p.id, name: p.name, barcode: p.barcode, ordered: "1", received: "1", damaged: "0", cost: "", batch_no: "", expiry: "" });
const cell = (err, align = "text-right") => `h-10 w-full rounded-lg border px-2 ${align} font-mono text-sm outline-none focus:border-slate-900 ${err ? "border-rose-400 bg-rose-50" : "border-slate-200"}`;

function Field({ label, err, id, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
      {children}
      {err && <span className="block text-[11px] font-semibold text-rose-600" data-testid={`grn-error-${id}`}>{err}</span>}
    </label>
  );
}

function LineRow({ l, i, set, remove, err, pharmacy }) {
  const short = Math.max(0, n(l.ordered) - n(l.received));
  const acc = n(l.received) - n(l.damaged);
  const e = (k) => err[`lines.${i}.${k}`];
  const num = (k, label) => (
    <Field label={label} err={e(k)} id={`${i}-${k}`}>
      <input inputMode={k === "cost" ? "decimal" : "numeric"} value={l[k]} onChange={(ev) => set(i, k, ev.target.value)} className={cell(e(k))} data-testid={`grn-line-${i}-${k}`} />
    </Field>
  );
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid={`grn-line-${i}`}>
      <div className="flex items-center gap-2">
        <span className="font-semibold text-slate-900">{l.name}</span>
        <span className="font-mono text-[11px] text-slate-400">{l.barcode}</span>
        <button type="button" onClick={() => remove(i)} className="ml-auto grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600" aria-label="Remove line" data-testid={`grn-line-${i}-remove`}><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {num("ordered", "Ordered")}{num("received", "Received")}{num("damaged", "Damaged")}{num("cost", "Cost / unit")}
      </div>
      {pharmacy && (
        <div className="mt-2 grid grid-cols-2 gap-2">
          <Field label="Batch no" err={e("batch_no")} id={`${i}-batch_no`}><input value={l.batch_no} onChange={(ev) => set(i, "batch_no", ev.target.value)} className={`${cell(e("batch_no"), "text-left")} uppercase`} data-testid={`grn-line-${i}-batch_no`} /></Field>
          <Field label="Expiry" err={e("expiry")} id={`${i}-expiry`}><input type="date" value={l.expiry} onChange={(ev) => set(i, "expiry", ev.target.value)} className={cell(e("expiry"))} data-testid={`grn-line-${i}-expiry`} /></Field>
        </div>
      )}
      <div className="mt-2 flex gap-4 font-mono text-xs">
        <span>Short <b data-testid={`grn-line-${i}-short`}>{short}</b></span>
        <span>Accepted <b className={acc < 0 ? "text-rose-600" : "text-emerald-700"} data-testid={`grn-line-${i}-accepted`}>{acc}</b></span>
      </div>
    </div>
  );
}

export function GrnEditor({ grn, onClose, onPosted }) {
  const { session } = useAuth();
  const biz = session.business;
  const pharmacy = biz.business_type === "pharmacy";
  const [g, setG] = useState(() => grn ? { ...grn, lines: grn.lines.map((l) => ({ ...l, ordered: String(l.ordered), received: String(l.received), damaged: String(l.damaged) })) } : { supplier: "", invoice_no: "", invoice_date: today(), lines: [] });
  const [id, setId] = useState(grn?.id || null);
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [q, setQ] = useState("");
  const scanRef = useRef(null);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data.products)); }, []);
  useEffect(() => { api.get("/inventory/suppliers", { params: { q: g.supplier } }).then((r) => setSuppliers(r.data)).catch(() => {}); }, [g.supplier]);
  const matches = useMemo(() => (q.trim().length < 2 ? [] : products.filter((p) => p.barcode === q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 6)), [q, products]);

  const addProduct = (p) => {
    setG((s) => {
      const i = s.lines.findIndex((l) => l.product_id === p.id && !pharmacy);
      if (i >= 0) return { ...s, lines: s.lines.map((l, j) => (j === i ? { ...l, ordered: String(n(l.ordered) + 1), received: String(n(l.received) + 1) } : l)) };
      return { ...s, lines: [...s.lines, blankLine(p)] };
    });
    setQ("");
  };
  const byCode = (code) => {
    const p = products.find((x) => x.barcode === code.trim());
    if (p) addProduct(p);
    else toast.error("Product not found");
  };
  useBarcodeScanner(byCode);
  const set = (i, k, v) => { setG((s) => ({ ...s, lines: s.lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)) })); setErr((e) => ({ ...e, [`lines.${i}.${k}`]: undefined })); };
  const remove = (i) => setG((s) => ({ ...s, lines: s.lines.filter((_, j) => j !== i) }));

  const totals = useMemo(() => {
    const t = { ordered: 0, received: 0, damaged: 0, short: 0, accepted: 0, cost: 0 };
    g.lines.forEach((l) => {
      t.ordered += n(l.ordered); t.received += n(l.received); t.damaged += n(l.damaged);
      t.short += Math.max(0, n(l.ordered) - n(l.received));
      const acc = n(l.received) - n(l.damaged);
      t.accepted += acc;
      try { t.cost += acc * toMinor(l.cost || "0", biz.decimals); } catch { /* invalid cost shows on save */ }
    });
    return { ...t, cost_str: formatMoney(t.cost, biz.decimals) };
  }, [g.lines, biz.decimals]);

  const save = async (confirm = false) => {
    const body = { supplier: g.supplier, invoice_no: g.invoice_no, invoice_date: g.invoice_date, lines: g.lines, confirm_duplicate: confirm };
    try {
      const { data } = id ? await api.put(`/inventory/grns/${id}`, body) : await api.post("/inventory/grns", body);
      setId(data.id);
      setErr({});
      return data;
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "DUPLICATE_INVOICE") {
        if (window.confirm(`${d.msg}. Save anyway?`)) return save(true);
        toast.warning(d.msg);
      } else if (d?.errors) setErr(d.errors);
      else toast.error(errMsg(e));
      if (d?.msg) toast.error(d.msg, { id: "grn-err" });
      return null;
    }
  };
  const run = async (post) => {
    setBusy(true);
    const saved = await save();
    if (saved && !post) toast.success(`${saved.number} saved as draft`);
    if (saved && post) {
      try {
        const { data } = await api.post(`/inventory/grns/${saved.id}/post`);
        toast.success(`${data.number} posted — ${data.totals.accepted} units waiting in RECEIVING`);
        onPosted(data);
      } catch (e) {
        const d = e?.response?.data?.detail;
        if (d?.errors) setErr(d.errors);
        toast.error(errMsg(e));
      }
    }
    setBusy(false);
  };
  const he = (k) => err[k];

  return (
    <div className="space-y-4 pb-24" data-testid="grn-editor">
      <div className="flex items-center gap-3">
        <button onClick={onClose} className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200" aria-label="Back" data-testid="grn-back"><ArrowLeft className="h-4 w-4" /></button>
        <div className="font-display text-xl font-bold">{grn?.number || "New GRN"} <span className="text-sm font-semibold text-slate-400">Draft</span></div>
      </div>
      <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:grid-cols-3">
        <Field label="Supplier" err={he("supplier")} id="supplier">
          <input list="grn-suppliers" value={g.supplier} onChange={(e) => setG({ ...g, supplier: e.target.value })} className={cell(he("supplier"), "text-left")} data-testid="grn-supplier" />
          <datalist id="grn-suppliers">{suppliers.map((s) => <option key={s} value={s} />)}</datalist>
        </Field>
        <Field label="Supplier invoice no" err={he("invoice_no")} id="invoice_no"><input value={g.invoice_no} onChange={(e) => setG({ ...g, invoice_no: e.target.value })} className={cell(he("invoice_no"), "text-left")} data-testid="grn-invoice-no" /></Field>
        <Field label="Invoice date" err={he("invoice_date")} id="invoice_date"><input type="date" value={g.invoice_date} onChange={(e) => setG({ ...g, invoice_date: e.target.value })} className={cell(he("invoice_date"))} data-testid="grn-invoice-date" /></Field>
      </div>
      <div className="relative flex gap-2">
        <div className="relative flex-1">
        <ScanBarcode className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
        <input ref={scanRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); matches.length === 1 || products.some((p) => p.barcode === q.trim()) ? addProduct(products.find((p) => p.barcode === q.trim()) || matches[0]) : byCode(q); } }}
          placeholder="Scan a barcode or search a product" className="h-12 w-full rounded-xl border-2 border-slate-900 bg-white pl-9 pr-3 text-sm outline-none" data-testid="grn-scan" />
        </div>
        <CameraScanButton continuous title="Scan received items" testid="grn-camera-button" onDetect={(raw) => {
          const code = codeVariants(raw).find((c) => products.some((p) => p.barcode === c)) || raw;
          const p = products.find((x) => x.barcode === code);
          byCode(code);
          return p ? { ok: true, label: p.name } : { ok: false, label: "Product not found" };
        }} />
        {matches.length > 0 && (
          <div className="absolute left-0 top-full z-20 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg" data-testid="grn-search-results">
            {matches.map((p) => <button key={p.id} type="button" onClick={() => addProduct(p)} className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-amber-50" data-testid={`grn-search-${p.id}`}><span className="flex-1">{p.name}</span><span className="font-mono text-xs text-slate-400">{p.barcode}</span></button>)}
          </div>
        )}
      </div>
      {he("lines") && <div className="text-sm font-semibold text-rose-600" data-testid="grn-error-lines">{he("lines")}</div>}
      <div className="space-y-2">{g.lines.map((l, i) => <LineRow key={i} l={l} i={i} set={set} remove={remove} err={err} pharmacy={pharmacy} />)}</div>
      <GrnTotals t={totals} currency={biz.currency} />
      <div className="sticky bottom-0 flex gap-2 border-t border-slate-200 bg-slate-50/95 py-3 backdrop-blur">
        <button disabled={busy} onClick={() => run(false)} className="h-12 flex-1 rounded-xl border-2 border-slate-900 font-bold disabled:opacity-50" data-testid="grn-save-draft">Save draft</button>
        <button disabled={busy || !g.lines.length} onClick={() => run(true)} className="h-12 flex-1 rounded-xl bg-amber-500 font-bold text-slate-950 disabled:opacity-50" data-testid="grn-post">Post GRN</button>
      </div>
    </div>
  );
}
